"use strict"

import { AssignmentFormData } from "./types"
import AuthService, {
  UserTokenInfo,
} from "../../app/services/auth/auth.service"
import AnalyticsService from "../../app/services/analytics/analytics.service"
import BreadcrumbService from "../../app/services/breadcrumb-service/breadcrumb.service"
import { HttpService } from "../../app/services/http/http.service"
import AssignmentService from "../../app/services/assignment/assignment.service"
import CacheService from "../../app/services/cache/cache.service"
import AssignmentTrackingService from "../../app/services/assignment-tracking/assignment-tracking.service"
import FirebaseService from "../../app/services/firebase/firebase.service"
import StorageService from "../../app/services/storage/storage.service"

import { AssignmentTags } from "../../app/model/domain/assignment"
import ElementMetadata, {
  ElementIntents,
} from "../../app/model/domain/element-metadata"
import { UserRoles } from "../../app/model/domain/user"
import Textbox from "../../app/model/ui/elements/textbox"
import Point from "../../app/model/ui/point"
import Size from "../../app/model/ui/size"
import SlideBackground from "../../app/model/ui/elements/slide-background"
import FillInTheBlankParent, {
  FillInTheBlankAnswer,
} from "../../app/model/ui/elements/fill-in-the-blank-parent"
import CkImage from "../../app/model/ui/elements/ckimage"
import { FitbAnswerTypes } from "../../app/components/assignment-toolbar/assignment-toolbar.directive"

// Background URL options
const BACKGROUND_URLS = [
  "https://storage.googleapis.com/slide-backgrounds/classkick_bagrounds_default_bg_pattern-2.png",
  "https://storage.googleapis.com/slide-backgrounds/classkick_bagrounds_default_bg_pattern-5.png",
  "https://storage.googleapis.com/slide-backgrounds/classkick_bagrounds_default_bg_pattern-6.png",
  "https://storage.googleapis.com/slide-backgrounds/classkick_bagrounds_default_bg_pattern-7.png",
]

// Placeholder image URL for slides that need an image but won't generate AI image
const PLACEHOLDER_IMAGE_URL =
  "https://www.googleapis.com/storage/v1/b/sheet-content-images/o/placeholder-image.png?alt=media"

// Image configuration
const IMAGE_CONFIG = {
  MAX_WIDTH: 400,
  POSITION: new Point(350, 550),
  PLACEHOLDER_SIZE: { width: 400, height: 300 },
} as const

// Student summary configuration
const STUDENT_SUMMARY_CONFIG = {
  FONT_SIZE: 30,
  POSITION: new Point(200, 200),
  BACKGROUND_COLOR: "#fcfcfd",
  BORDER_COLOR: "#149962",
} as const

interface AiChatResponse {
  choices: Array<{
    message: {
      content: string
    }
  }>
}

interface ParsedQuestion {
  text: string
  suggestedAnswers?: string[]
  imagePrompt?: string
}

interface Question {
  id: string
}

interface Assignment {
  id: string
  name: string
  questions: Question[]
}

interface ConversationMessage {
  role: "user" | "assistant"
  content: string
}

export class AssignmentWizardController {
  private user: UserTokenInfo
  private assignmentId?: string
  private cachedQuestionsResponse?: string
  private conversationHistory: ConversationMessage[] = []
  private currentFormData?: AssignmentFormData

  constructor(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    private environment: any, // TODO: migrate to typescript
    private AnalyticsService: AnalyticsService,
    private AuthService: AuthService,
    private AssignmentService: AssignmentService,
    private CacheService: CacheService,
    private AssignmentTrackingService: AssignmentTrackingService,
    private FirebaseService: FirebaseService,
    private BreadcrumbService: BreadcrumbService,
    private HttpService: HttpService,
    private StorageService: StorageService,
  ) {
    "ngInject"

    this.user = this.AuthService.authData

    this.restoreFromStorage()

    // Bind methods for React components
    this.createAssignment = this.createAssignment.bind(this)
    this.generateSummary = this.generateSummary.bind(this)
    this.refineAssignment = this.refineAssignment.bind(this)
    this.resetSummaryGeneratedCache = this.resetSummaryGeneratedCache.bind(this)
    this.closeWizard = this.closeWizard.bind(this)
    this.viewAssignment = this.viewAssignment.bind(this)
    this.saveToStorage = this.saveToStorage.bind(this)
    this.getStoredFormData = this.getStoredFormData.bind(this)
    this.getStoredConversationHistory =
      this.getStoredConversationHistory.bind(this)
    this.getStoredCachedResponse = this.getStoredCachedResponse.bind(this)
    this.updateFormData = this.updateFormData.bind(this)

    if (!this.user?.isTeacher) {
      this.BreadcrumbService.go("root.account.home")
    }
  }

  /**
   * Restore state from localStorage on controller initialization
   */
  private restoreFromStorage(): void {
    const storedFormData = this.StorageService
      .assignmentWizardFormData as AssignmentFormData | null
    const storedHistory = this.StorageService
      .assignmentWizardConversationHistory as ConversationMessage[] | null
    const storedResponse = this.StorageService
      .assignmentWizardCachedResponse as string | null

    if (storedFormData) {
      this.currentFormData = storedFormData
    }

    if (storedHistory && Array.isArray(storedHistory)) {
      this.conversationHistory = storedHistory
    }

    if (storedResponse) {
      this.cachedQuestionsResponse = storedResponse
    }
  }

  /**
   * Save current state to localStorage
   */
  saveToStorage(): void {
    this.StorageService.assignmentWizardFormData = (this.currentFormData ||
      null) as any // eslint-disable-line @typescript-eslint/no-explicit-any
    this.StorageService.assignmentWizardConversationHistory = (this
      .conversationHistory || null) as any // eslint-disable-line @typescript-eslint/no-explicit-any
    this.StorageService.assignmentWizardCachedResponse = (this
      .cachedQuestionsResponse || null) as any // eslint-disable-line @typescript-eslint/no-explicit-any
  }

  /**
   * Update form data and save to storage (for React component)
   */
  updateFormData(data: AssignmentFormData): void {
    this.currentFormData = data
    this.saveToStorage()
  }

  /**
   * Get stored form data (for React component initialization)
   */
  getStoredFormData(): AssignmentFormData | undefined {
    const stored = this.StorageService
      .assignmentWizardFormData as AssignmentFormData | null
    return stored || undefined
  }

  /**
   * Get stored conversation history (for React component initialization)
   */
  getStoredConversationHistory(): ConversationMessage[] {
    const stored = this.StorageService.assignmentWizardConversationHistory as
      | ConversationMessage[]
      | null
    return stored && Array.isArray(stored) ? stored : []
  }

  /**
   * Get stored cached response (for React component initialization)
   */
  getStoredCachedResponse(): string | undefined {
    const stored = this.StorageService.assignmentWizardCachedResponse as
      | string
      | null
    return stored || undefined
  }

  /**
   * Clear all wizard data from localStorage
   */
  private clearStorage(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.StorageService.assignmentWizardFormData = null as any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.StorageService.assignmentWizardConversationHistory = null as any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.StorageService.assignmentWizardCachedResponse = null as any
  }

  async generateSummary(data: AssignmentFormData): Promise<string> {
    // Store form data for refinement requests
    this.currentFormData = data

    const prompt = this.buildPrompt(data)
    const response = await this.callAICompletionAssistant(prompt)

    this.conversationHistory = [
      { role: "user", content: prompt },
      { role: "assistant", content: response },
    ]

    // Cache the response for later use in createAssignment
    this.cachedQuestionsResponse = response

    // Save to localStorage after updating state
    this.saveToStorage()

    const parsed = this.parseResponse(response, data)
    const { questions } = parsed
    let summary = parsed.summary

    // Show a preview of few of the questions generated
    summary += `\n\nQuestions Preview:\n`
    questions.slice(0, 3).forEach((q, i) => {
      const preview =
        q.text.length > 100 ? q.text.slice(0, 100) + "..." : q.text
      summary += `\n${i + 1}. ${preview}`
    })

    if (questions.length > 3) {
      summary += `\n\n...and ${questions.length - 3} more question${questions.length - 3 > 1 ? "s" : ""}.`
    }

    this.trackGenerateSummary()

    return summary
  }

  async refineAssignment(userFeedback: string): Promise<string> {
    if (!this.currentFormData) {
      throw new Error("No assignment data available for refinement")
    }

    this.conversationHistory.push({
      role: "user",
      content: userFeedback,
    })

    const response = await this.callAICompletionAssistantWithContext(
      this.conversationHistory,
    )

    this.conversationHistory.push({
      role: "assistant",
      content: response,
    })

    this.cachedQuestionsResponse = response

    this.saveToStorage()

    const parsed = this.parseResponse(response, this.currentFormData)
    const { questions } = parsed
    let summary = parsed.summary

    // Show a preview of the questions
    summary += `\n\nQuestions Preview:\n`
    questions.slice(0, 3).forEach((q, i) => {
      const preview =
        q.text.length > 100 ? q.text.slice(0, 100) + "..." : q.text
      summary += `\n${i + 1}. ${preview}`
    })

    if (questions.length > 3) {
      summary += `\n\n...and ${questions.length - 3} more question${questions.length - 3 > 1 ? "s" : ""}.`
    }

    return summary
  }

  async createAssignment(data: AssignmentFormData): Promise<void> {
    // Use cached response if available, otherwise generate new one
    let response: string
    if (this.cachedQuestionsResponse) {
      response = this.cachedQuestionsResponse
    } else {
      const prompt = this.buildPrompt(data)
      response = await this.callAICompletionAssistant(prompt)
    }

    const { questions, assignmentName, studentSummary } = this.parseResponse(
      response,
      data,
    )
    const assignment = await this.createAssignmentWithQuestions(
      data,
      questions,
      assignmentName,
    )

    this.assignmentId = assignment.id

    await this.addElementsToQuestions(assignment, questions, studentSummary)

    this.trackCtaClick()

    // reset cached response
    this.cachedQuestionsResponse = undefined
  }

  private async createAssignmentWithQuestions(
    data: AssignmentFormData,
    questions: ParsedQuestion[],
    assignmentName: string,
  ): Promise<Assignment> {
    // assignment_tag value field is varchar(190), choose 100 char to be safe
    const assignmentTags = [
      {
        name: AssignmentTags.AI_GENERATED,
        data: {
          created_by_ai: true,
          practice_type: this.truncateValue(data.practiceType, 100),
          grade_level: this.truncateValue(data.grade, 100),
          topic: this.truncateValue(data.practiceTopic, 100),
        },
      },
    ]

    // Create an extra slide for the student summary (slide 0)
    // Questions will be on slides 1, 2, 3, etc.
    const totalSlides = questions.length + 1

    const newAssignment = await this.AssignmentService.create(
      {
        name: assignmentName,
        tags: assignmentTags,
      },
      totalSlides,
    )
    const assignment =
      await this.CacheService.addAssignmentForUser(newAssignment)

    this.AssignmentTrackingService.target = assignment
    return assignment
  }

  private async addElementsToQuestions(
    assignment: Assignment,
    questions: ParsedQuestion[],
    studentSummary?: string,
  ): Promise<void> {
    const promises: Promise<void>[] = []

    // Add student summary to slide 0 (first slide)
    if (studentSummary) {
      promises.push(
        this.addStudentSummarySlide(assignment.questions[0], studentSummary),
      )
    }

    // Add questions starting from slide 1 (index 1 in assignment.questions)
    assignment.questions
      .slice(1)
      .forEach((question: Question, index: number) => {
        const slideIndex = index + 1 // Slide 1, 2, 3, etc.
        const questionData = questions[slideIndex - 1] // Map slide to question: slide 1 = questions[0]
        promises.push(
          this.addElementsToQuestion(
            question,
            questionData,
            slideIndex,
            questions,
          ),
        )
      })

    await Promise.all(promises)
  }

  /**
   * Add student summary to the first slide (slide 0)
   */
  private async addStudentSummarySlide(
    question: Question,
    studentSummary: string,
  ): Promise<void> {
    const metadata = this.createElementMetadata()
    const slideIndex = 0

    // Add background
    await this.addSlideBackground(slideIndex, metadata)

    // Add student summary textbox
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const summaryTextbox: any = new Textbox(
      this.FirebaseService.newId(),
      metadata,
      studentSummary,
      STUDENT_SUMMARY_CONFIG.POSITION,
      undefined, // size
      STUDENT_SUMMARY_CONFIG.FONT_SIZE,
      undefined, // padding
      undefined, // colorSpans
      undefined, // activeColor
      STUDENT_SUMMARY_CONFIG.BACKGROUND_COLOR,
      STUDENT_SUMMARY_CONFIG.BORDER_COLOR,
      undefined, // fontFamily
    )

    await this.AssignmentTrackingService.createElement(
      summaryTextbox,
      slideIndex,
      false,
    )
  }

  private async addElementsToQuestion(
    question: Question,
    questionData: ParsedQuestion,
    slideIndex: number,
    allQuestions: ParsedQuestion[],
  ): Promise<void> {
    if (!questionData) return

    const metadata = this.createElementMetadata()

    await this.addSlideBackground(slideIndex, metadata)

    // Handle images: Generate AI image for first question with imagePrompt
    // use placeholder for other questions with imagePrompt
    if (questionData.imagePrompt) {
      // slideIndex 1 = questions[0], slideIndex 2 = questions[1], etc.
      const questionArrayIndex = slideIndex - 1
      const isFirstImageQuestion = this.isFirstQuestionWithImage(
        allQuestions,
        questionArrayIndex,
      )

      if (isFirstImageQuestion) {
        // Generate AI image for the first question that needs an image
        await this.addGeneratedImage(
          questionData.imagePrompt,
          slideIndex,
          metadata,
        )
      } else {
        // Add placeholder image for subsequent questions that needs an image
        await this.addPlaceholderImage(slideIndex, metadata)
      }
    }

    await this.addTextbox(questionData.text, slideIndex, metadata)

    // Add fitb only if we have suggested answers
    if (questionData.suggestedAnswers?.length) {
      const answers = questionData.suggestedAnswers.map(
        (answer) => new FillInTheBlankAnswer(answer, 5, false),
      )
      await this.addFillInTheBlank(answers, slideIndex, metadata)
    }
  }

  /**
   * Check if this is the first question in the questions array that has an image prompt
   * We only want to generate AI image for the first question that needs an image
   */
  private isFirstQuestionWithImage(
    allQuestions: ParsedQuestion[],
    currentQuestionIndex: number,
  ): boolean {
    if (!allQuestions[currentQuestionIndex]?.imagePrompt) {
      return false
    }

    for (let i = 0; i < currentQuestionIndex; i++) {
      if (allQuestions[i]?.imagePrompt) {
        return false
      }
    }

    return true
  }

  private async addGeneratedImage(
    imagePrompt: string,
    questionIndex: number,
    metadata: ElementMetadata,
  ): Promise<void> {
    try {
      const response = await this.HttpService.authPost<{
        imageUrl: string
        width: number
        height: number
      }>(`${this.environment.serverUrlBase}/v1/assistant/generate-image`, {
        prompt: imagePrompt,
        elementId: this.FirebaseService.newId(),
      })

      const { imageUrl, width, height } = response

      // Calculate scale to fit nicely on canvas
      const scale =
        width > IMAGE_CONFIG.MAX_WIDTH ? IMAGE_CONFIG.MAX_WIDTH / width : 1

      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      const image: any = new CkImage(
        this.FirebaseService.newId(),
        metadata,
        IMAGE_CONFIG.POSITION,
        new Size(width, height),
        scale,
        0, // rotation
        imageUrl,
      )

      await this.AssignmentTrackingService.createElement(
        image,
        questionIndex,
        false, // isStudentElement
      )
    } catch (error) {
      console.error("Failed to generate/add AI image:", error)
      // Continue without image if generation fails
    }
  }

  private async addPlaceholderImage(
    questionIndex: number,
    metadata: ElementMetadata,
  ): Promise<void> {
    try {
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
      const image: any = new CkImage(
        this.FirebaseService.newId(),
        metadata,
        IMAGE_CONFIG.POSITION,
        new Size(
          IMAGE_CONFIG.PLACEHOLDER_SIZE.width,
          IMAGE_CONFIG.PLACEHOLDER_SIZE.height,
        ),
        1, // scale
        0, // rotation
        PLACEHOLDER_IMAGE_URL,
      )

      await this.AssignmentTrackingService.createElement(
        image,
        questionIndex,
        false, // isStudentElement
      )
    } catch (error) {
      console.error("Failed to add placeholder image:", error)
      // Continue without image if adding placeholder fails
    }
  }

  private async addSlideBackground(
    questionIndex: number,
    metadata: ElementMetadata,
  ): Promise<void> {
    const backgroundUrl = this.getRandomBackgroundUrl()

    // TODO: migrate to typescript
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    const slideBackground: any = new SlideBackground(
      this.FirebaseService.newId(),
      metadata,
      undefined,
      backgroundUrl, // url
    )

    await this.AssignmentTrackingService.createElement(
      slideBackground,
      questionIndex,
      false, // isStudentElement
    )
  }

  /**
   * Get a random background URL from the available options
   */
  private getRandomBackgroundUrl(): string {
    const randomIndex = Math.floor(Math.random() * BACKGROUND_URLS.length)
    return BACKGROUND_URLS[randomIndex]
  }

  private async addTextbox(
    text: string,
    questionIndex: number,
    metadata: ElementMetadata,
  ): Promise<void> {
    // TODO: migrate to typescript
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    const textbox: any = new Textbox(
      this.FirebaseService.newId(),
      metadata,
      text,
      new Point(150, 200),
      undefined, // size
      21, // fontSize
      undefined, // padding
      undefined, // colorSpans
      undefined, // activeColor
      undefined, // backgroundColor
      "#149962", // borderColor
      undefined, // fontFamily
    )

    await this.AssignmentTrackingService.createElement(
      textbox,
      questionIndex,
      false,
    )
  }

  private async addFillInTheBlank(
    answers: FillInTheBlankAnswer[],
    questionIndex: number,
    metadata: ElementMetadata,
  ): Promise<void> {
    // TODO: migrate to typescript
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    const fitb: any = new FillInTheBlankParent(
      this.FirebaseService.newId(),
      metadata,
      new Point(550, 200),
      undefined, // size
      answers,
      21, // fontSize
      FitbAnswerTypes.PLAIN.value,
      "none", // precheck
      null, // questionMetadataId
    )

    await this.AssignmentTrackingService.createElement(
      fitb,
      questionIndex,
      false,
    )
  }

  private createElementMetadata(): ElementMetadata {
    return new ElementMetadata(
      this.user.id,
      UserRoles.TEACHER,
      ElementIntents.CONTENT,
      "1", // version
    )
  }

  private buildPrompt(data: AssignmentFormData): string {
    const additionalContext = data.additionalConsiderations
      ? `\n\nADDITIONAL CONSIDERATIONS: ${data.additionalConsiderations}`
      : ""

    return `Create an educational assignment based on the user's request.

USER REQUEST: "${data.practiceTopic}"

SLIDE COUNT INSTRUCTIONS:
- If the user explicitly mentioned a number of slides/questions in their request, use that EXACT number (up to a maximum of 30)
- If the user requested more than 30 slides, create exactly 30 and MUST include a note in the Summary explaining: "Note: You requested [X] slides, but we've capped it at 30 for optimal performance and usability."
- If the user did NOT specify a number, create exactly 8 slides
- Do NOT exceed 30 slides under any circumstances

SPECIFICATIONS:
- Grade Level: ${data.grade}
- Time: ${data.classTime}
- Curriculum: ${data.curriculum.join(", ")}
- Practice Type: ${data.practiceType}${additionalContext}

REQUIREMENTS:
- Age-appropriate for ${data.grade} grade
- Clear, unambiguous language
- Mix of difficulty levels (40% foundational, 40% intermediate, 20% advanced)
- Questions support the learning topic
- Completable within ${data.classTime}
- Use PLAIN TEXT ONLY - no LaTeX, no markdown math formatting, no escape characters
- Write all mathematical expressions using standard keyboard characters (e.g., "x + 3/4 = 2" not "( x + frac{3}{4} = 2 )" or any LaTeX math notations)
- Use unicode symbols when needed (×, ÷, ², ³, √, π, etc.) but prefer simple text${
      data.additionalConsiderations
        ? `
- Incorporate the additional considerations: ${data.additionalConsiderations}`
        : ""
    }

IMAGE GENERATION:
- For ANY question where a visual diagram, illustration, or image would significantly help explain the concept, include an image generation prompt
- Only include image prompts where a purely visual representation would genuinely enhance understanding of the question
- NOT every question needs an image - only include when it adds real educational value

ABSOLUTE IMAGE REQUIREMENTS (CRITICAL):
- Images MUST be PURELY VISUAL with ABSOLUTELY NO TEXT, NO LETTERS, NO NUMBERS, NO LABELS, NO WORDS of any kind
- The image should visually represent the concept in a way that complements the question text
- DO NOT EVER include text, labels, captions, annotations, letters, numbers, or words in the image prompt
- All educational context should be in the question text itself, NOT in the image
- Think: "Can this concept be shown visually without any written language?" If no, don't include an image prompt

IMAGE PROMPT GUIDELINES:
- Image prompts should be descriptive, specific, age-appropriate, and suitable for DALL-E
- Focus on clear visual representations: objects, scenes, diagrams, illustrations, real-world items
- Format: **Image Prompt:** [detailed description of VISUAL ONLY elements - NO TEXT ALLOWED]
- Explicitly state "no text, no labels, no numbers" in the prompt itself to reinforce this requirement

EXAMPLES OF GOOD IMAGE PROMPTS (ABSOLUTELY NO TEXT):
  * "A colorful illustration of a butterfly with detailed wings showing patterns and antennae, no text, no labels"
  * "A simple diagram of the water cycle showing clouds, rain, a body of water, and evaporation arrows, purely visual with no text or labels"
  * "A photorealistic image of the solar system with planets in correct relative positions, no text, no numbers, no labels"
  * "An illustration of a red apple next to an orange, simple and clear, no text or labels"

EXAMPLES OF BAD IMAGE PROMPTS (CONTAIN TEXT - NEVER USE THESE):
  * "A number line from 1 to 10 with numbers labeled" ❌ Contains numbers
  * "A diagram of plant parts with labels for roots, stem, and leaves" ❌ Contains text labels
  * "A poster showing the alphabet with letters A-Z" ❌ Contains letters
  * "A clock showing 3:30" ❌ Contains numbers
  * "A map with country names" ❌ Contains text

STUDENT SUMMARY REQUIREMENT:
- Create a brief, engaging, student-friendly summary (2-3 sentences max) that explains what the assignment is about
- Write in a warm, encouraging tone appropriate for ${data.grade} grade students
- Focus on what students will learn or practice, not on difficulty levels or pedagogical details
- Keep it simple, clear, and motivating
- Format: **Student Summary:** [2-3 sentences for students]

FORMAT (follow exactly):
**Assignment Name:** [Create a concise, engaging name max 80 characters - be creative and descriptive]

**Summary:** [Create a 3-4 sentence engaging summary that describes the assignment. If slides were capped at 30, START the summary with the capping note. MUST include: (1) the assignment name, (2) the exact difficulty distribution (e.g., "3 foundational, 3 intermediate, 2 advanced questions"), (3) the learning approach (guided answers vs open-ended), and (4) curriculum alignment. Make it sound natural and compelling for teachers.]

**Student Summary:** [Create a 2-3 sentence student-friendly summary explaining what the assignment is about in simple, encouraging language]

**Question 1** [Difficulty: Foundational/Intermediate/Advanced]
[Question text]
**Image Prompt:** [OPTIONAL - only if helpful: detailed DALL-E prompt for VISUAL-ONLY educational illustration with ABSOLUTELY NO TEXT, NO LABELS, NO NUMBERS]
**Suggested Answers:** [answer1, answer2, answer3]

**Question 2** [Difficulty: Foundational/Intermediate/Advanced]
[Question text]
**Image Prompt:** [OPTIONAL - only if helpful and purely visual]
**Suggested Answers:** [answer1, answer2]

Continue for all questions...

CRITICAL FORMATTING RULES: 
- Assignment name must be on the first line
- Summary must be on the second line immediately after assignment name
- Student Summary must be on the third line immediately after summary
- Summary should be engaging and informative, highlighting key aspects of the assignment
- Student Summary should be warm, simple, and encouraging for students
- If slides were capped at 30, the Summary MUST start with the capping note
- Image Prompt is OPTIONAL and can appear on ANY question where it would help
- Image Prompt MUST describe a purely visual image with ABSOLUTELY NO TEXT, NO LABELS, NO WORDS, NO NUMBERS
- In the image prompt description itself, explicitly include phrases like "no text, no labels, no numbers"
- Image should be detailed enough to generate a clear educational illustration without any text
- Suggested Answers MUST be enclosed in square brackets: [answer1, answer2]
- Only include "Suggested Answers:" line for questions with specific, factual answers
- Omit "Suggested Answers:" entirely for open-ended or creative writing prompts
- Each suggested answer must be concise (maximum 20 characters)
- Separate multiple answers with commas inside the brackets
- Assignment name should be professional, clear, and include grade level

EXAMPLES:
**Assignment Name:** Spider Biology Basics - Grade 3

**Summary:** Spider Biology Basics introduces third graders to arachnid anatomy and behavior through 5 carefully designed questions. This assignment includes 2 foundational, 2 intermediate, and 1 advanced question, mixing guided answers with open-ended responses to encourage both factual recall and creative thinking. Aligned with Next Generation Science Standards for elementary life science.

**Student Summary:** In this assignment, you'll learn amazing facts about spiders! You'll discover how spiders look, how they move, and what makes them special creatures.

**Question 1** [Difficulty: Foundational]
How many legs does a spider have?
**Image Prompt:** A detailed, colorful illustration of a garden spider viewed from above, showing its eight legs clearly spread out, body segments, and simple patterns, no text, no labels, no numbers
**Suggested Answers:** [8, eight]

**Question 2** [Difficulty: Intermediate]
Where do most spiders build their webs?
**Image Prompt:** An illustration of a spider web between tree branches in a garden setting, purely visual with no text or labels
**Suggested Answers:** [trees, bushes, corners]

**Question 3** [Difficulty: Intermediate]
Solve for x: x + 3/4 = 2
**Suggested Answers:** [5/4, 1.25]

**Question 4** [Difficulty: Advanced]
Describe what you learned about photosynthesis.
(no Suggested Answers line for open-ended questions)`
  }

  private async callAICompletionAssistant(prompt: string): Promise<string> {
    const response = await this.HttpService.authPost<{
      content: AiChatResponse
    }>(`${this.environment.serverUrlBase}/v1/assistant/completion`, { prompt })

    return response.content.choices[0]?.message?.content || ""
  }

  private async callAICompletionAssistantWithContext(
    messages: ConversationMessage[],
  ): Promise<string> {
    const response = await this.HttpService.authPost<{
      content: AiChatResponse
    }>(
      `${this.environment.serverUrlBase}/v1/assistant/completion-with-context`,
      {
        messages,
      },
    )

    return response.content.choices[0]?.message?.content || ""
  }

  private parseResponse(
    aiText: string,
    data: AssignmentFormData,
  ): {
    questions: ParsedQuestion[]
    assignmentName: string
    summary: string
    studentSummary?: string
  } {
    let assignmentName = ""
    let summary = ""
    let studentSummary = ""
    let questionsText = aiText

    // Extract assignment name
    const nameMatch = aiText.match(/\*\*Assignment Name:\*\*\s*(.+?)(?:\n|$)/i)
    if (nameMatch) {
      assignmentName = nameMatch[1]
        .trim()
        .replace(/^["']|["']$/g, "") // Remove quotes
        .substring(0, 80) // Ensure max 80 chars for db entry

      // Remove the assignment name line from the text
      questionsText = aiText.replace(
        /\*\*Assignment Name:\*\*\s*.+?(?:\n|$)/i,
        "",
      )
    }

    // Extract summary
    const summaryMatch = questionsText.match(
      /\*\*Summary:\*\*\s*([\s\S]+?)(?:\n\n|\*\*Student Summary|\*\*Question)/i,
    )
    if (summaryMatch) {
      summary = summaryMatch[1].trim()

      // Remove the summary from the text
      questionsText = questionsText.replace(
        /\*\*Summary:\*\*\s*[\s\S]+?(?:\n\n|\*\*Student Summary|\*\*Question)/i,
        (match) => {
          if (match.includes("**Student Summary")) {
            return "**Student Summary"
          }
          if (match.includes("**Question")) {
            return "**Question"
          }
          return ""
        },
      )
    }

    // Extract student summary
    const studentSummaryMatch = questionsText.match(
      /\*\*Student Summary:\*\*\s*([\s\S]+?)(?:\n\n|\*\*Question)/i,
    )
    if (studentSummaryMatch) {
      studentSummary = studentSummaryMatch[1].trim()

      // Remove the student summary from the text
      questionsText = questionsText.replace(
        /\*\*Student Summary:\*\*\s*[\s\S]+?(?:\n\n|\*\*Question)/i,
        "**Question",
      )
    }

    // Parse questions
    const questions = this.parseQuestions(questionsText)

    // Fallback to generic name if parsing failed
    if (!assignmentName) {
      assignmentName = this.buildAssignmentName(data)
    }

    return { questions, assignmentName, summary, studentSummary }
  }

  private parseQuestions(aiText: string): ParsedQuestion[] {
    const questionRegex =
      /\*\*Question \d+\*\*[^\n]*\n([\s\S]*?)(?=\*\*Question \d+\*\*|$)/g
    const questions: ParsedQuestion[] = []

    let match: RegExpExecArray | null
    while ((match = questionRegex.exec(aiText)) !== null) {
      const parsed = this.parseQuestionBlock(match[1])
      if (parsed) {
        questions.push(parsed)
      }
    }

    // Fallback: use entire text as single question if parsing failed
    return questions.length > 0 ? questions : [{ text: aiText.trim() }]
  }

  private parseQuestionBlock(block: string): ParsedQuestion | null {
    const lines = block.split("\n").filter((line) => line.trim())
    if (lines.length === 0) return null

    let text = ""
    let suggestedAnswers: string[] = []
    let imagePrompt: string | undefined

    for (const line of lines) {
      const trimmed = line.trim()

      // Skip difficulty markers
      if (trimmed.match(/^\[Difficulty:/i)) continue

      // Extract image prompt
      const imageMatch = trimmed.match(
        /^\*\*Image Prompt(?:\s+for\s+Question\s+\d+)?:\*\*\s*(.+)/i,
      )
      if (imageMatch) {
        imagePrompt = imageMatch[1].trim()
        continue
      }

      // Extract suggested answers
      const answerMatch = trimmed.match(
        /^\*\*Suggested Answers:\*\*\s*\[(.*)\]/i,
      )
      if (answerMatch) {
        suggestedAnswers = answerMatch[1]
          .split(",")
          .map((a) => a.trim().substring(0, 20)) // Max 20 characters for FITB
          .filter((a) => a.length > 0)
        continue
      }

      // Add to question text
      if (trimmed) {
        text += (text ? "\n" : "") + trimmed
      }
    }

    return text
      ? {
          text: text.trim(),
          suggestedAnswers:
            suggestedAnswers.length > 0 ? suggestedAnswers : undefined,
          imagePrompt,
        }
      : null
  }

  private buildAssignmentName(data: AssignmentFormData): string {
    const name =
      `${data.practiceTopic || "Practice"} - ${data.grade || ""}`.trim()
    return this.truncateValue(name, 80)
  }

  private truncateValue(value: string | undefined, maxLength: number): string {
    if (!value) return ""
    return value.length > maxLength ? value.substring(0, maxLength) : value
  }

  // Mixpanel event to track summary generation
  private trackGenerateSummary() {
    this.AnalyticsService.sendEvent({
      eventTag: "assignment_wizard_generate_questions",
      properties: {
        userEmail: this.user.email,
        summary: this.cachedQuestionsResponse,
      },
    })
  }

  // Mixpanel event to track file uploads
  private trackFileUpload(files: File[]) {
    this.AnalyticsService.sendEvent({
      eventTag: "assignment_wizard_files_uploaded",
      properties: {
        userEmail: this.user.email,
        files: files.map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
        })),
      },
    })
  }

  // Mixpanel event to track assignment generation click
  private trackCtaClick() {
    this.AnalyticsService.sendEvent({
      eventTag: "assignment_wizard_generate_questions_clicked",
      properties: {
        userEmail: this.user.email,
        assignmentId: this.assignmentId,
        summary: this.cachedQuestionsResponse,
        conversationHistory: this.conversationHistory,
      },
    })
  }

  async generateAssignmentFromUploadedFiles(
    files: File[],
  ): Promise<AiChatResponse> {
    const formData = new FormData()

    files.forEach((file) => {
      formData.append("files", file)
    })

    const response = await this.HttpService.authPostMultipart<{
      content: AiChatResponse
    }>(
      `${this.environment.serverUrlBase}/v1/assistant/completion-generation-from-files`,
      formData,
    )

    this.trackFileUpload(files)
    return response.content
  }

  setCachedResponse(response: string): void {
    this.cachedQuestionsResponse = response
    this.conversationHistory = [
      { role: "user", content: "Generated from uploaded files" },
      { role: "assistant", content: response },
    ]

    this.saveToStorage()
  }

  parseResponseFromFiles(aiText: string): Partial<AssignmentFormData> {
    try {
      const metadata = JSON.parse(aiText)

      const result: Partial<AssignmentFormData> = {
        practiceTopic: metadata.practiceTopic || "",
        grade: metadata.gradeLevel || "",
        classTime: metadata.classTime || "",
        curriculum: this.parseCurriculum(metadata.curriculum || ""),
        practiceType: metadata.practiceType || "",
        additionalConsiderations: metadata.additionalConsiderations || "",
      }

      return result
    } catch (error) {
      throw new Error("Failed to parse uploaded file response")
    }
  }

  /**
   * Converts a comma-separated string into a trimmed array of strings.
   */
  private parseCurriculum(curriculumInput: string | string[]): string[] {
    if (Array.isArray(curriculumInput)) {
      return curriculumInput.map((s) => s.trim()).filter(Boolean)
    }

    if (typeof curriculumInput !== "string" || !curriculumInput.trim()) {
      return []
    }

    return curriculumInput
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
  }

  resetSummaryGeneratedCache(): void {
    this.cachedQuestionsResponse = undefined
    this.conversationHistory = []
  }

  closeWizard(): void {
    this.clearStorage()
    this.BreadcrumbService.go("root.account.nav.assignments")
  }

  viewAssignment(): void {
    if (this.assignmentId) {
      this.BreadcrumbService.go("root.account.assignment", {
        assignmentId: this.assignmentId,
      })
    }
  }
}
